import React, { useState, useEffect, useCallback, useRef } from 'react';
import ApiClient, { IPEntry, IPListType, ASNStats, PaginatedResponse } from '../api/client';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { usePermissions } from '../context/PermissionContext';
import { DataTableShell, LocalTabs, ModalShell, PageHeader, SortableTableHeader } from './ui';
import { AlertIcon, ArrowDownIcon, ArrowUpIcon, GlobeIcon, ShieldIcon, TrashIcon } from './icons';

interface IPManagementProps {
  apiClient: ApiClient;
  onNavigateToSubmissions?: (params: { ipId?: number; asn?: number }) => void;
}

type TabType = 'ips' | 'asn';
type IPSortKey = 'ipAddress' | 'lastSeenAt' | 'submissionCount' | 'createdAt';
type ASNSortKey = 'asn' | 'asnName' | 'uniqueShadowbannedIps' | 'totalSubmissions';
type SortDirection = 'asc' | 'desc';

const getListTypeClass = (listType: IPListType) => {
  if (listType === 'whitelist') return 'status-whitelisted';
  if (listType === 'blacklist') return 'status-blacklisted';
  return 'status-badge-neutral';
};

const getDispositionClass = (disposition: ASNStats['disposition']) => {
  if (disposition === 'good') return 'status-approved';
  if (disposition === 'bad') return 'status-spam';
  return 'status-badge-neutral';
};

const IPManagement: React.FC<IPManagementProps> = ({ apiClient, onNavigateToSubmissions }) => {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('ip.manage');

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('ips');

  // IP List state
  const [entries, setEntries] = useState<IPEntry[]>([]);
  const [ipPagination, setIPPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [ipLoading, setIPLoading] = useState(true);

  // ASN Stats state
  const [asnStats, setASNStats] = useState<ASNStats[]>([]);
  const [asnPagination, setASNPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [asnLoading, setASNLoading] = useState(false);
  const [shadowbannedOnly, setShadowbannedOnly] = useState(false);

  // Shared state
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Filter
  const [filterType, setFilterType] = useState<IPListType | ''>('');
  const [filterASN, setFilterASN] = useState<number | null>(null);
  const [ipSort, setIPSort] = useState<{ key: IPSortKey; direction: SortDirection }>({
    key: 'lastSeenAt',
    direction: 'desc',
  });
  const [asnSort, setASNSort] = useState<{ key: ASNSortKey; direction: SortDirection }>({
    key: 'uniqueShadowbannedIps',
    direction: 'desc',
  });

  // Add IP form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newIP, setNewIP] = useState('');
  const [newListType, setNewListType] = useState<IPListType>('whitelist');
  const [newReason, setNewReason] = useState('');
  const [newPermanent, setNewPermanent] = useState(false);
  const [adding, setAdding] = useState(false);

  // Remove confirmation
  const [removingEntry, setRemovingEntry] = useState<IPEntry | null>(null);

  // Stats
  const [stats, setStats] = useState({ whitelisted: 0, blacklisted: 0 });

  // Shadowban state
  const [shadowbanningASN, setShadowbanningASN] = useState<number | null>(null);
  const [shadowbanReason, setShadowbanReason] = useState('');
  const [showShadowbanModal, setShowShadowbanModal] = useState(false);
  const [selectedASNForShadowban, setSelectedASNForShadowban] = useState<ASNStats | null>(null);

  // Debounce search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  const DEFAULT_LIMIT = 25;

  // Load IP entries
  const loadIPEntries = useCallback(async (page = 1) => {
    setIPLoading(true);
    setError(null);
    try {
      const response = await apiClient.listIPEntries({
        page,
        limit: ipPagination?.limit || DEFAULT_LIMIT,
        search: debouncedSearch || undefined,
        listType: filterType || undefined,
        asn: filterASN || undefined,
        sortBy: ipSort.key,
        sortDir: ipSort.direction,
      });
      setEntries(response.data || []);
      if (response.pagination) {
        setIPPagination(response.pagination);
      }

      // Also load stats
      const statsData = await apiClient.getIPStats();
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load IP entries');
    } finally {
      setIPLoading(false);
    }
  }, [apiClient, ipPagination?.limit, debouncedSearch, filterType, filterASN, ipSort]);

  // Load ASN stats
  const loadASNStats = useCallback(async (page = 1) => {
    setASNLoading(true);
    setError(null);
    try {
      const response = await apiClient.getASNStats({
        page,
        limit: asnPagination?.limit || DEFAULT_LIMIT,
        search: debouncedSearch || undefined,
        shadowbannedOnly: shadowbannedOnly || undefined,
        sortBy: asnSort.key,
        sortDir: asnSort.direction,
      });
      setASNStats(response.data || []);
      if (response.pagination) {
        setASNPagination(response.pagination);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ASN stats');
    } finally {
      setASNLoading(false);
    }
  }, [apiClient, asnPagination?.limit, debouncedSearch, shadowbannedOnly, asnSort]);

  // Load data when tab/filters change
  useEffect(() => {
    if (activeTab === 'ips') {
      loadIPEntries(1);
    } else {
      loadASNStats(1);
    }
  }, [activeTab, debouncedSearch, filterType, filterASN, shadowbannedOnly, ipSort, asnSort]);

  // Initial load
  useEffect(() => {
    loadIPEntries(1);
  }, []);

  // Escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showAddForm) setShowAddForm(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showAddForm]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newIP.trim()) return;

    setAdding(true);
    setError(null);
    try {
      if (newListType === 'whitelist') {
        await apiClient.whitelistIP(newIP.trim(), newReason.trim() || undefined);
      } else {
        await apiClient.blacklistIP(newIP.trim(), newReason.trim() || undefined, newPermanent);
      }
      setSuccess(`IP ${newIP} added to ${newListType}`);
      setNewIP('');
      setNewReason('');
      setNewPermanent(false);
      setShowAddForm(false);
      loadIPEntries(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add IP');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async () => {
    if (!removingEntry) return;

    try {
      await apiClient.removeIP(removingEntry.id);
      setSuccess(`IP ${removingEntry.ipAddress} removed from all lists`);
      loadIPEntries(ipPagination.page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove IP');
    } finally {
      setRemovingEntry(null);
    }
  };

  const handleMoveToOtherList = async (entry: IPEntry) => {
    try {
      const newType: IPListType = entry.listType === 'whitelist' ? 'blacklist' : 'whitelist';
      if (newType === 'whitelist') {
        await apiClient.whitelistIP(entry.ipAddress, `Moved from blacklist`);
      } else {
        await apiClient.blacklistIP(entry.ipAddress, `Moved from whitelist`, false);
      }
      setSuccess(`IP ${entry.ipAddress} moved to ${newType}`);
      loadIPEntries(ipPagination.page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to move IP');
    }
  };

  const handleASNClick = (asn: number) => {
    setFilterASN(asn);
    setActiveTab('ips');
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const handleRefresh = () => {
    if (activeTab === 'ips') {
      loadIPEntries(ipPagination.page);
    } else {
      loadASNStats(asnPagination.page);
    }
  };

  const handleShadowban = async () => {
    if (!selectedASNForShadowban) return;
    setShadowbanningASN(selectedASNForShadowban.asn);
    setError(null);
    try {
      await apiClient.shadowbanASN(selectedASNForShadowban.asn, shadowbanReason || undefined);
      setSuccess(`ASN ${selectedASNForShadowban.asn} (${selectedASNForShadowban.asnName}) has been shadowbanned`);
      setShowShadowbanModal(false);
      setSelectedASNForShadowban(null);
      setShadowbanReason('');
      loadASNStats(asnPagination.page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to shadowban ASN');
    } finally {
      setShadowbanningASN(null);
    }
  };

  const handleRemoveShadowban = async (asn: ASNStats) => {
    setShadowbanningASN(asn.asn);
    setError(null);
    try {
      await apiClient.removeShadowban(asn.asn);
      setSuccess(`Shadowban removed from ASN ${asn.asn} (${asn.asnName})`);
      loadASNStats(asnPagination.page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove shadowban');
    } finally {
      setShadowbanningASN(null);
    }
  };

  const openShadowbanModal = (asn: ASNStats) => {
    setSelectedASNForShadowban(asn);
    setShadowbanReason('');
    setShowShadowbanModal(true);
  };

  const updateIPSort = (key: IPSortKey) => {
    setIPSort(current => current.key === key
      ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: key === 'lastSeenAt' || key === 'submissionCount' ? 'desc' : 'asc' });
  };

  const updateASNSort = (key: ASNSortKey) => {
    setASNSort(current => current.key === key
      ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: key === 'uniqueShadowbannedIps' || key === 'totalSubmissions' ? 'desc' : 'asc' });
  };

  return (
    <div className="manager-page ip-management-page">
      {error && (
        <div className="alert alert-error">
          <AlertIcon size={15} />
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="alert-dismiss"
            aria-label="Dismiss error"
          >
            ✕
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span>✓</span>
          <span>{success}</span>
          <button
            onClick={() => setSuccess(null)}
            className="alert-dismiss"
            aria-label="Dismiss success"
          >
            ✕
          </button>
        </div>
      )}

      <PageHeader
        title="IP Management"
        actions={
          <>
            <button className="btn btn-secondary" onClick={handleRefresh}>
              Refresh
            </button>
            {canManage && activeTab === 'ips' && (
              <button className="btn btn-primary" onClick={() => setShowAddForm(true)}>
                + Add IP
              </button>
            )}
          </>
        }
      />

      <div className="ip-management-shell">
        <LocalTabs
          tabs={[
            { id: 'ips', label: 'IP Addresses' },
            { id: 'asn', label: 'ASN Networks' },
          ]}
          active={activeTab}
          onChange={(tab) => {
            setActiveTab(tab);
            if (tab === 'ips') setFilterASN(null);
          }}
          filters={activeTab === 'ips' && (
            <div className="local-tabs-filters" aria-label="IP list filters">
              <button
                type="button"
                className={`ip-filter-tab ${filterType === 'whitelist' ? 'active' : ''}`}
                onClick={() => setFilterType(filterType === 'whitelist' ? '' : 'whitelist')}
              >
                <span>Whitelisted</span>
                <strong className="count-badge count-badge-success">{stats.whitelisted}</strong>
              </button>
              <button
                type="button"
                className={`ip-filter-tab ${filterType === 'blacklist' ? 'active' : ''}`}
                onClick={() => setFilterType(filterType === 'blacklist' ? '' : 'blacklist')}
              >
                <span>Blacklisted</span>
                <strong className="count-badge count-badge-danger">{stats.blacklisted}</strong>
              </button>
            </div>
          )}
        />

        {/* Search and Filters */}
        <div className="ip-control-row">
          <input
            type="text"
            placeholder={activeTab === 'ips' ? 'Search IP, reason, or ASN...' : 'Search ASN number or name...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {activeTab === 'ips' && (
            <>
              {filterASN && (
                <div className="ip-filter-pill">
                  <span>ASN: {filterASN}</span>
                  <button
                    onClick={() => setFilterASN(null)}
                    aria-label="Clear ASN filter"
                  >
                    ✕
                  </button>
                </div>
              )}
            </>
          )}
          {activeTab === 'asn' && (
            <>
              <label className="ip-checkbox-filter">
                <input
                  type="checkbox"
                  checked={shadowbannedOnly}
                  onChange={(e) => setShadowbannedOnly(e.target.checked)}
                />
                <span>Shadowbanned only</span>
              </label>
            </>
          )}
        </div>

        {activeTab === 'ips' && (
          <div className="ip-tab-panel">
            {/* IP List */}
            {ipLoading ? (
              <div className="empty-state">
                <div className="loading-spinner"></div>
                <p>Loading IP entries...</p>
              </div>
            ) : entries.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon"><ShieldIcon size={28} /></div>
                <h3>No IP entries</h3>
                <p>{filterType ? `No ${filterType}ed IPs` : 'Add IPs to whitelist or blacklist'}</p>
              </div>
            ) : (
              <>
                <DataTableShell className="ip-table-wrap">
                  <table className="data-table ip-data-table">
                    <thead>
                      <tr>
                        <SortableTableHeader active={ipSort.key === 'ipAddress'} direction={ipSort.direction} onClick={() => updateIPSort('ipAddress')}>IP Address</SortableTableHeader>
                        <th>List</th>
                        <th>ASN</th>
                        <th>Reason</th>
                        <th className="ip-center-cell">Perm</th>
                        <SortableTableHeader active={ipSort.key === 'lastSeenAt'} direction={ipSort.direction} onClick={() => updateIPSort('lastSeenAt')}>Last Seen</SortableTableHeader>
                        <SortableTableHeader active={ipSort.key === 'submissionCount'} direction={ipSort.direction} onClick={() => updateIPSort('submissionCount')} align="right">Subs</SortableTableHeader>
                        <th className="actions-cell">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map(entry => (
                        <tr key={entry.id}>
                          <td className="mono-cell">
                            {onNavigateToSubmissions ? (
                              <span
                                className="ip-link"
                                onClick={() => onNavigateToSubmissions({ ipId: entry.id })}
                                title="View submissions from this IP"
                              >
                                {entry.ipAddress}
                              </span>
                            ) : entry.ipAddress}
                          </td>
                          <td>
                            <span className={`status-badge ${getListTypeClass(entry.listType)}`}>
                              {entry.listType}
                            </span>
                          </td>
                          <td className="ip-asn-cell">
                            {entry.asn ? (
                              <span
                                className="ip-link"
                                onClick={() => handleASNClick(entry.asn!)}
                                title={entry.asnName || undefined}
                              >
                                {entry.asn}
                                {entry.asnName && <span className="ip-asn-name">({entry.asnName.slice(0, 15)})</span>}
                              </span>
                            ) : (
                              <span className="ip-empty-marker">-</span>
                            )}
                          </td>
                          <td className="ip-reason-cell">
                            {entry.reason || '-'}
                          </td>
                          <td className="ip-center-cell">
                            {entry.isPermanent ? (
                              <span className="ip-locked-marker">🔒</span>
                            ) : (
                              <span className="ip-empty-marker">-</span>
                            )}
                          </td>
                          <td className={entry.lastSeenAt ? 'ip-date-cell' : 'data-muted'}>
                            {formatDate(entry.lastSeenAt)}
                          </td>
                          <td className="ip-count-cell">
                            {entry.submissionCount ?? 0}
                          </td>
                          <td className="actions-cell">
                            {canManage && (
                              <div className="row-actions">
                                <button
                                  className="icon-btn"
                                  onClick={() => handleMoveToOtherList(entry)}
                                  title={`Move to ${entry.listType === 'whitelist' ? 'blacklist' : 'whitelist'}`}
                                  aria-label={`Move to ${entry.listType === 'whitelist' ? 'blacklist' : 'whitelist'}`}
                                >
                                  {entry.listType === 'whitelist' ? <ArrowDownIcon /> : <ArrowUpIcon />}
                                </button>
                                <button
                                  className="icon-btn intent-danger"
                                  onClick={() => setRemovingEntry(entry)}
                                  title="Remove from all lists"
                                  aria-label="Remove from all lists"
                                >
                                  <TrashIcon />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DataTableShell>

                {/* Pagination */}
                {ipPagination.totalPages > 1 && (
                  <div className="pagination-row split">
                    <span>
                      Showing {((ipPagination.page - 1) * ipPagination.limit) + 1}-{Math.min(ipPagination.page * ipPagination.limit, ipPagination.total)} of {ipPagination.total}
                    </span>
                    <div className="pagination-actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        disabled={ipPagination.page <= 1}
                        onClick={() => loadIPEntries(ipPagination.page - 1)}
                      >
                        ← Prev
                      </button>
                      <span>
                        Page {ipPagination.page} of {ipPagination.totalPages}
                      </span>
                      <button
                        className="btn btn-sm btn-secondary"
                        disabled={ipPagination.page >= ipPagination.totalPages}
                        onClick={() => loadIPEntries(ipPagination.page + 1)}
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'asn' && (
          <div className="ip-tab-panel">
            {/* ASN List */}
            {asnLoading ? (
              <div className="empty-state">
                <div className="loading-spinner"></div>
                <p>Loading ASN stats...</p>
              </div>
            ) : asnStats.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon"><GlobeIcon size={28} /></div>
                <h3>No ASN data</h3>
                <p>{shadowbannedOnly ? 'No shadowbanned networks' : 'No ASN data available'}</p>
              </div>
            ) : (
              <>
                <DataTableShell className="ip-table-wrap">
                  <table className="data-table ip-data-table">
                    <thead>
                      <tr>
                        <SortableTableHeader active={asnSort.key === 'asn'} direction={asnSort.direction} onClick={() => updateASNSort('asn')}>ASN</SortableTableHeader>
                        <SortableTableHeader active={asnSort.key === 'asnName'} direction={asnSort.direction} onClick={() => updateASNSort('asnName')}>Network Name</SortableTableHeader>
                        <SortableTableHeader active={asnSort.key === 'uniqueShadowbannedIps'} direction={asnSort.direction} onClick={() => updateASNSort('uniqueShadowbannedIps')} align="right">Shadowbanned IPs</SortableTableHeader>
                        <SortableTableHeader active={asnSort.key === 'totalSubmissions'} direction={asnSort.direction} onClick={() => updateASNSort('totalSubmissions')} align="right">Total Submissions</SortableTableHeader>
                        <th className="ip-center-cell">Status</th>
                        <th className="actions-cell">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {asnStats.map(asn => (
                        <tr key={asn.asn} className={asn.shadowbanned ? 'asn-row-banned' : ''}>
                          <td className="mono-cell">
                            {onNavigateToSubmissions ? (
                              <span
                                className="ip-link"
                                onClick={() => onNavigateToSubmissions({ asn: asn.asn })}
                                title="View submissions from this ASN"
                              >
                                {asn.asn}
                              </span>
                            ) : asn.asn}
                          </td>
                          <td className="data-muted">
                            {asn.asnName || '-'}
                          </td>
                          <td className={`ip-count-cell ${asn.uniqueShadowbannedIps > 0 ? 'tone-danger' : ''}`}>
                            {asn.uniqueShadowbannedIps}
                          </td>
                          <td className="ip-count-cell">
                            {asn.totalSubmissions}
                          </td>
                          <td className="ip-center-cell">
                            <div className="asn-status-stack">
                              {/* Disposition badge */}
                              <span
                                className={`status-badge ${getDispositionClass(asn.disposition)}`}
                                title={
                                  asn.disposition === 'uncharacterized'
                                    ? `Need ${5 - asn.uniqueIps} more IPs to characterize`
                                    : asn.disposition === 'good'
                                    ? `${Math.round((1 - asn.uniqueShadowbannedIps / asn.uniqueIps) * 100)}% clean`
                                    : `${Math.round((asn.uniqueShadowbannedIps / asn.uniqueIps) * 100)}% spam`
                                }
                              >
                                {asn.disposition === 'uncharacterized' ? 'UNCHARACTERIZED' : asn.disposition === 'good' ? 'GOOD' : 'BAD'}
                              </span>
                              {/* Shadowban indicator */}
                              {asn.shadowbanned && (
                                <span
                                  className="status-badge status-banned"
                                  title={asn.shadowbanReason || 'Shadowbanned'}
                                >
                                  BANNED{asn.banCount > 1 ? ` ${asn.banCount}x` : ''}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="actions-cell">
                            <div className="row-actions">
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => handleASNClick(asn.asn)}
                                title="View IPs from this ASN"
                              >
                                View IPs
                              </button>
                              {canManage && (
                                asn.shadowbanned ? (
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() => handleRemoveShadowban(asn)}
                                    disabled={shadowbanningASN === asn.asn}
                                    title="Remove shadowban"
                                  >
                                    {shadowbanningASN === asn.asn ? '...' : 'Unban'}
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => openShadowbanModal(asn)}
                                    disabled={shadowbanningASN === asn.asn}
                                    title="Shadowban this ASN"
                                  >
                                    Ban
                                  </button>
                                )
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DataTableShell>

                {/* Pagination */}
                {asnPagination.totalPages > 1 && (
                  <div className="pagination-row split">
                    <span>
                      Showing {((asnPagination.page - 1) * asnPagination.limit) + 1}-{Math.min(asnPagination.page * asnPagination.limit, asnPagination.total)} of {asnPagination.total}
                    </span>
                    <div className="pagination-actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        disabled={asnPagination.page <= 1}
                        onClick={() => loadASNStats(asnPagination.page - 1)}
                      >
                        ← Prev
                      </button>
                      <span>
                        Page {asnPagination.page} of {asnPagination.totalPages}
                      </span>
                      <button
                        className="btn btn-sm btn-secondary"
                        disabled={asnPagination.page >= asnPagination.totalPages}
                        onClick={() => loadASNStats(asnPagination.page + 1)}
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Add IP Modal */}
      {showAddForm && (
        <ModalShell
          title="Add IP"
          onClose={() => setShowAddForm(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowAddForm(false)}
                disabled={adding}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="add-ip-form"
                className="btn btn-primary"
                disabled={adding || !newIP.trim()}
              >
                {adding ? 'Adding...' : `Add to ${newListType}`}
              </button>
            </>
          }
        >
          <form id="add-ip-form" onSubmit={handleAdd}>
            <div className="form-group">
              <label>IP Address</label>
              <input
                type="text"
                value={newIP}
                onChange={e => setNewIP(e.target.value)}
                placeholder="e.g., 192.168.1.1"
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>List Type</label>
              <select
                value={newListType}
                onChange={e => setNewListType(e.target.value as IPListType)}
              >
                <option value="whitelist">Whitelist (auto-approve)</option>
                <option value="blacklist">Blacklist (reject submissions)</option>
              </select>
            </div>
            <div className="form-group">
              <label>Reason (optional)</label>
              <input
                type="text"
                value={newReason}
                onChange={e => setNewReason(e.target.value)}
                placeholder="e.g., Trusted partner"
              />
            </div>
            {newListType === 'blacklist' && (
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={newPermanent}
                    onChange={e => setNewPermanent(e.target.checked)}
                  />
                  Permanent blacklist
                </label>
                <small>Permanent entries cannot be automatically removed.</small>
              </div>
            )}
          </form>
        </ModalShell>
      )}

      {/* Remove Confirmation */}
      {removingEntry && (
        <ConfirmDeleteModal
          title="Remove IP"
          message={`Remove ${removingEntry.ipAddress} from all lists? This IP will no longer be whitelisted or blacklisted.`}
          confirmButtonLabel="Remove"
          onConfirm={handleRemove}
          onCancel={() => setRemovingEntry(null)}
        />
      )}

      {/* Shadowban Modal */}
      {showShadowbanModal && selectedASNForShadowban && (
        <ModalShell
          title="Shadowban ASN"
          onClose={() => setShowShadowbanModal(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowShadowbanModal(false)}
                disabled={shadowbanningASN !== null}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleShadowban}
                disabled={shadowbanningASN !== null}
              >
                {shadowbanningASN !== null ? 'Banning...' : 'Shadowban'}
              </button>
            </>
          }
        >
          <p className="modal-copy">
            Shadowban <strong>AS{selectedASNForShadowban.asn}</strong> ({selectedASNForShadowban.asnName || 'Unknown'})?
          </p>
          <p className="modal-help">
            All submissions from this network will be silently discarded. The sender will not be notified.
          </p>
          <div className="form-group">
            <label>Reason (optional)</label>
            <input
              type="text"
              value={shadowbanReason}
              onChange={e => setShadowbanReason(e.target.value)}
              placeholder="e.g., Russian spam network"
              autoFocus
            />
          </div>
        </ModalShell>
      )}
    </div>
  );
};

export default IPManagement;
